import { useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { debounceTime, Subject } from 'rxjs';
import { MailboxFilter, NOT_EVALUATED_FILTER } from '../../db/MailboxFilter.js';
import type { MailSyncStatus } from '../../db/MailSyncStatus.js';
import {
  MAIL_PROCESSING_EVALUATION_OPTIONS,
  MAIL_PROCESSING_NOT_EVALUATED_DOT,
  MAIL_PROCESSING_NOT_EVALUATED_LABEL,
} from '../../features/information-extraction/db/MailExtractionResult.js';

// Rating filter options (multiselect): every evaluation plus "not evaluated". Wording and
// dot colours come from the shared rating list, so a new category shows up here by itself.
export const EVALUATION_OPTIONS: { value: string; label: string; description?: string; dot: string }[] = [
  ...MAIL_PROCESSING_EVALUATION_OPTIONS.map(o => ({
    value: o.value as string,
    label: o.label,
    description: o.description,
    dot: o.dot,
  })),
  { value: NOT_EVALUATED_FILTER, label: MAIL_PROCESSING_NOT_EVALUATED_LABEL, dot: MAIL_PROCESSING_NOT_EVALUATED_DOT },
];

interface ActiveChip {
  label: string;
  dot?: string;
  onRemove: () => void;
}

// Callbacks the removable chips invoke to clear their respective filter.
interface ChipActions {
  clearSap: () => void;
  toggleBool: (key: 'hasComments' | 'validationFailed') => void;
  setShowArchived: (v: boolean) => void;
  toggleEvaluation: (value: string) => void;
}

// One removable chip per active advanced filter, mirroring the badge count.
const buildActiveChips = (filter: MailboxFilter, actions: ChipActions): ActiveChip[] => {
  const chips: ActiveChip[] = [];
  if (filter.sapNumber) chips.push({ label: `SAP: ${filter.sapNumber}`, onRemove: actions.clearSap });
  if (filter.hasComments) chips.push({ label: 'Hat Notizen', onRemove: () => actions.toggleBool('hasComments') });
  if (filter.validationFailed)
    chips.push({ label: 'Validierung fehlgeschlagen', onRemove: () => actions.toggleBool('validationFailed') });
  if (filter.showArchived)
    chips.push({ label: 'Archivierte anzeigen', onRemove: () => actions.setShowArchived(false) });
  (filter.evaluations ?? []).forEach(value => {
    const opt = EVALUATION_OPTIONS.find(o => o.value === value);
    if (opt) chips.push({ label: opt.label, dot: opt.dot, onRemove: () => actions.toggleEvaluation(value) });
  });
  return chips;
};

const renderActiveChips = (chips: ActiveChip[]) =>
  chips.length
    ? html`<div class="flex flex-wrap gap-1.5">
        ${chips.map(
          c =>
            html`<span
              class="inline-flex items-center gap-1.5 h-6 pl-2.5 pr-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-medium"
            >
              ${c.dot
                ? html`<span class="h-1.5 w-1.5 rounded-full shrink-0" style="background-color:${c.dot}"></span>`
                : nothing}
              ${c.label}
              <button
                type="button"
                class="flex rounded-full p-0.5 hover:bg-primary/15"
                title="Filter entfernen"
                @click=${c.onRemove}
              >
                <x-icon class="text-[13px]">close</x-icon>
              </button>
            </span>`,
        )}
      </div>`
    : nothing;

// Property pill toggle (Eigenschaften): a chip that fills with the accent and shows
// a check when active — replaces the former checkbox so options read as a tappable set.
const renderTogglePill = (label: string, on: boolean, onClick: () => void) => html`
  <button
    type="button"
    class="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border text-xs font-medium transition-colors ${on
      ? 'bg-primary/10 border-primary/30 text-primary'
      : 'border-base-300 text-base-content/70 hover:bg-base-200/60'}"
    @click=${onClick}
  >
    ${on ? html`<x-icon class="text-[13px]">check</x-icon>` : nothing}${label}
  </button>
`;

// Rating pill toggle (Bewertung): same chip shape, tinted with the rating's own
// colour (background + border + dot) when active so the palette stays recognisable;
// text keeps the readable default tone since the coloured dot already carries identity.
const renderEvalPill = (opt: (typeof EVALUATION_OPTIONS)[number], on: boolean, onClick: () => void) => html`
  <button
    type="button"
    title=${opt.description ?? opt.label}
    class="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border text-xs font-medium transition-colors ${on
      ? 'text-base-content'
      : 'border-base-300 text-base-content/70 hover:bg-base-200/60'}"
    style=${on
      ? `background-color:color-mix(in srgb,${opt.dot} 13%,transparent);border-color:color-mix(in srgb,${opt.dot} 38%,transparent)`
      : nothing}
    @click=${onClick}
  >
    <span class="h-2 w-2 rounded-full shrink-0" style="background-color:${opt.dot}"></span>
    ${opt.label}
  </button>
`;

// Collapsible advanced filter panel (SAP number, properties, rating). The bar stays
// visible with a count badge and reset link; the controls below collapse away while
// the active-filter chips remain on screen.
const renderAdvancedFilters = ({
  filter,
  activeFilterCount,
  open,
  activeChips,
  onToggleOpen,
  onReset,
  onChange,
  onToggleBool,
  onShowArchived,
  onToggleEvaluation,
}: {
  filter: MailboxFilter;
  activeFilterCount: number;
  open: boolean;
  activeChips: ActiveChip[];
  onToggleOpen: () => void;
  onReset: () => void;
  onChange: (patch: Partial<MailboxFilter>) => void;
  onToggleBool: (key: 'hasComments' | 'validationFailed') => void;
  onShowArchived: (v: boolean) => void;
  onToggleEvaluation: (value: string) => void;
}) => html`
  <div class="flex flex-col gap-2">
    <div class="flex items-center gap-2">
      <button
        type="button"
        class="flex items-center gap-1.5 text-xs font-medium text-base-content/55 hover:text-base-content"
        @click=${onToggleOpen}
      >
        <x-icon class="text-sm">tune</x-icon> Filter
        ${activeFilterCount
          ? html`<span
              class="inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-primary text-primary-content text-[10px] font-semibold leading-none"
              title="${activeFilterCount} Filter aktiv"
              >${activeFilterCount}</span
            >`
          : nothing}
        <x-icon class="text-sm transition-transform ${open ? 'rotate-180' : ''}">expand_more</x-icon>
      </button>
      ${activeFilterCount
        ? html`<button type="button" class="ml-auto text-xs text-base-content/45 hover:text-primary" @click=${onReset}>
            Zurücksetzen
          </button>`
        : nothing}
    </div>
    ${renderActiveChips(activeChips)}
    ${open
      ? html`<div class="flex flex-col gap-3 pt-2 border-t border-base-200">
          <input
            type="text"
            class="input input-bordered input-sm w-full"
            .value=${filter.sapNumber || ''}
            @input=${(e: Event) => onChange({ sapNumber: (e.target as HTMLInputElement).value })}
            placeholder="SAP-Nummer"
          />
          <div>
            <div class="text-[10.5px] font-semibold uppercase tracking-wider text-base-content/40 mb-2">
              Eigenschaften
            </div>
            <div class="flex flex-wrap gap-2">
              ${renderTogglePill('Hat Notizen', !!filter.hasComments, () => onToggleBool('hasComments'))}
              ${renderTogglePill('Validierung fehlgeschlagen', !!filter.validationFailed, () =>
                onToggleBool('validationFailed'),
              )}
              ${renderTogglePill('Archivierte anzeigen', !!filter.showArchived, () =>
                onShowArchived(!filter.showArchived),
              )}
            </div>
          </div>
          <div>
            <div class="text-[10.5px] font-semibold uppercase tracking-wider text-base-content/40 mb-2">Bewertung</div>
            <div class="flex flex-wrap gap-2">
              ${EVALUATION_OPTIONS.map(opt =>
                renderEvalPill(opt, filter.evaluations?.includes(opt.value) || false, () =>
                  onToggleEvaluation(opt.value),
                ),
              )}
            </div>
          </div>
        </div>`
      : nothing}
  </div>
`;

// Slim filter header: search up front, the me-mode toggle, then the collapsible
// advanced filters with their active-filter chips.
/** Coarse "how long ago" — the question here is minutes vs. hours, never seconds. */
const formatAge = (value: Date) => {
  const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60000);
  if (minutes < 1) return 'gerade eben';
  if (minutes < 60) return `vor ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `vor ${hours} h`;
  return `vor ${Math.round(hours / 24)} Tagen`;
};

/**
 * The mailbox poll can stop dead while everything else keeps working, and then no request
 * arrives at all. This line is the only place that difference is visible without the server log.
 */
const renderSyncState = (status: MailSyncStatus | null | undefined) => {
  if (!status) return nothing;
  const problem = status.stalledSince
    ? `Postfach-Abruf hängt seit ${formatAge(status.stalledSince)}`
    : status.lastError
      ? `Postfach-Abruf fehlgeschlagen: ${status.lastError}`
      : null;
  return html`
    <div class="flex items-center gap-1.5 text-xs ${problem ? 'text-error' : 'text-base-content/50'}">
      <x-icon class="text-sm">${problem ? 'error' : 'schedule'}</x-icon>
      <span
        >${problem ??
        (status.lastSuccessAt
          ? `Postfach abgerufen ${formatAge(status.lastSuccessAt)}`
          : 'Postfach noch nicht abgerufen')}</span
      >
    </div>
  `;
};

export const renderMailFilterPanel = ({
  filter,
  activeFilterCount,
  filtersOpen,
  activeChips,
  onSearch,
  onMineOnly,
  onToggleFilters,
  onReset,
  onChange,
  onToggleBool,
  onShowArchived,
  onToggleEvaluation,
  onRefresh,
  cooldown,
  syncStatus,
}: {
  filter: MailboxFilter;
  activeFilterCount: number;
  filtersOpen: boolean;
  activeChips: ActiveChip[];
  onSearch: (value: string) => void;
  onMineOnly: (v: boolean) => void;
  onToggleFilters: () => void;
  onReset: () => void;
  onChange: (patch: Partial<MailboxFilter>) => void;
  onToggleBool: (key: 'hasComments' | 'validationFailed') => void;
  onShowArchived: (v: boolean) => void;
  onToggleEvaluation: (value: string) => void;
  onRefresh?: () => void;
  cooldown?: number | null;
  syncStatus?: MailSyncStatus | null;
}) => html`
  <div class="py-3 border-b border-base-200 flex flex-col gap-2 shrink-0">
    <div class="flex items-center gap-2">
      <label class="input input-bordered input-sm flex items-center gap-2 grow">
        <x-icon class="text-sm text-base-content/40">search</x-icon>
        <input
          type="text"
          class="grow"
          .value=${filter.search || ''}
          @input=${(e: Event) => onSearch((e.target as HTMLInputElement).value)}
          placeholder="Suche Mail, Firma, Produkt…"
        />
      </label>
      ${onRefresh
        ? html`<button
            type="button"
            class="btn btn-ghost btn-sm btn-square shrink-0"
            title="Postfach aktualisieren"
            ?disabled=${typeof cooldown === 'number'}
            @click=${onRefresh}
          >
            ${typeof cooldown === 'number'
              ? html`<span class="text-sm font-semibold tabular-nums">${cooldown}</span>`
              : html`<x-icon class="text-base">refresh</x-icon>`}
          </button>`
        : nothing}
    </div>
    ${renderSyncState(syncStatus)}
    <label class="flex items-center justify-between gap-2 cursor-pointer text-sm">
      <span class="flex items-center gap-1.5 text-base-content/70">
        <x-icon class="text-sm">person</x-icon> Nur meine Anfragen
      </span>
      <input
        type="checkbox"
        class="toggle toggle-sm toggle-primary"
        .checked=${filter.mineOnly ?? false}
        @change=${(e: Event) => onMineOnly((e.target as HTMLInputElement).checked)}
      />
    </label>
    ${renderAdvancedFilters({
      filter,
      activeFilterCount,
      open: filtersOpen,
      activeChips,
      onToggleOpen: onToggleFilters,
      onReset,
      onChange,
      onToggleBool,
      onShowArchived,
      onToggleEvaluation,
    })}
  </div>
`;

// All mailbox-filter state and handlers in one place: the live filter, its debounced
// text inputs, immediate toggles, reset, and the props the panel renders from.
export const useMailboxFilter = () => {
  // Me-mode defaults on: a user sees only the requests they forwarded into the
  // system (sender address == their account address; resolved server-side).
  const [filter, setFilter] = useState(new MailboxFilter({ mineOnly: true }), []);

  // Debounced text inputs only (search + SAP number); all other filters toggle immediately.
  const filterSubject$ = new Subject<Partial<MailboxFilter>>();
  filterSubject$.pipe(debounceTime(250)).subscribe(values => {
    if (values.sapNumber !== undefined) filter.sapNumber = values.sapNumber;
    if (values.search !== undefined) filter.search = values.search;
    setFilter(filter);
  });
  const [currentValues] = useState<Partial<MailboxFilter>>({ sapNumber: undefined, search: undefined }, []);
  const emit = (patch: Partial<MailboxFilter>) => {
    Object.assign(currentValues, patch);
    filterSubject$.next({ ...currentValues });
  };

  const setMineOnly = (mineOnly: boolean) => {
    filter.mineOnly = mineOnly;
    setFilter(filter);
  };
  const setShowArchived = (showArchived: boolean) => {
    filter.showArchived = showArchived;
    setFilter(filter);
  };
  // Evaluation multiselect: toggle membership immediately (mutating the live filter
  // so rapid toggles accumulate correctly, unlike the debounced text fields).
  const toggleEvaluation = (value: string) => {
    const current = filter.evaluations ?? [];
    filter.evaluations = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
    setFilter(filter);
  };
  const toggleBool = (key: 'hasComments' | 'validationFailed') => {
    filter[key] = !filter[key];
    setFilter(filter);
  };
  const clearSap = () => {
    filter.sapNumber = undefined;
    currentValues.sapNumber = undefined;
    setFilter(filter);
  };
  // "Zurücksetzen": clear every advanced filter at once; search and me-mode are
  // separate, always-visible controls and stay untouched.
  const reset = () => {
    filter.sapNumber = undefined;
    filter.hasComments = false;
    filter.validationFailed = false;
    filter.showArchived = false;
    filter.evaluations = [];
    currentValues.sapNumber = undefined;
    setFilter(filter);
  };

  // Collapsed by default to keep the sidebar compact; the active-filter chips below
  // the bar keep what's in effect visible without expanding the controls.
  const [filtersOpen, setFiltersOpen] = useState(false, []);

  // Count of advanced filters in effect (search has its own always-visible box and is
  // excluded), shown as a badge so the active filters are visible even when collapsed.
  const activeFilterCount =
    (filter.sapNumber ? 1 : 0) +
    (filter.hasComments ? 1 : 0) +
    (filter.validationFailed ? 1 : 0) +
    (filter.showArchived ? 1 : 0) +
    (filter.evaluations?.length ?? 0);

  const activeChips = buildActiveChips(filter, { clearSap, toggleBool, setShowArchived, toggleEvaluation });

  const panel = {
    filter,
    activeFilterCount,
    filtersOpen,
    activeChips,
    onSearch: (value: string) => emit({ search: value }),
    onMineOnly: setMineOnly,
    onToggleFilters: () => setFiltersOpen(!filtersOpen),
    onReset: reset,
    onChange: emit,
    onToggleBool: toggleBool,
    onShowArchived: setShowArchived,
    onToggleEvaluation: toggleEvaluation,
  };

  return { filter, panel };
};
